import React, { useEffect, useMemo, useRef, useState } from 'react';

declare global {
  interface Window {
    kakao: any;
  }
}

interface KakaoMapViewerProps {
  data: KakaoOverlayPayload | null;
  height?: string;
  className?: string;
}

export interface KakaoPoint {
  x: number;
  y: number;
  lat?: number;
  lng?: number;
}

export interface KakaoPolygonOverlay {
  type: 'polygon';
  points: KakaoPoint[];
  coordinate: string;
  options?: KakaoOverlayOptions;
}

export interface KakaoRectangleOverlay {
  type: 'rectangle';
  sPoint: KakaoPoint;
  ePoint: KakaoPoint;
  coordinate: string;
  options?: KakaoOverlayOptions;
}

export interface KakaoCircleOverlay {
  type: 'circle';
  center?: KakaoPoint;
  sPoint?: KakaoPoint;
  ePoint?: KakaoPoint;
  coordinate: string;
  radius?: number;
  options?: KakaoOverlayOptions;
}

export interface KakaoOverlayOptions {
  strokeColor?: string;
  strokeWeight?: number;
  strokeStyle?: string;
  strokeOpacity?: number;
  fillColor?: string;
  fillOpacity?: number;
}

export interface KakaoOverlayPayload {
  polygon?: KakaoPolygonOverlay[];
  rectangle?: KakaoRectangleOverlay[];
  circle?: KakaoCircleOverlay[];
  marker?: unknown[];
  arrow?: unknown[];
  polyline?: unknown[];
}

const DEFAULT_CENTER = { lat: 37.5665, lng: 126.978 };
const DEFAULT_LEVEL = 3;

const loadKakaoMapsSdk = async (libraries: string[] = []): Promise<void> => {
  const apiKey = import.meta.env.VITE_KAKAO_APPKEY;

  if (!apiKey || apiKey === 'your_kakao_app_key_here') {
    throw new Error('카카오맵 API 키가 설정되지 않았습니다.');
  }

  if (window.kakao && window.kakao.maps) {
    return;
  }

  const existingScript = document.querySelector<HTMLScriptElement>(
    'script[data-kakao-map-sdk="true"]'
  );

  if (existingScript) {
    await new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('카카오맵 SDK 로드 실패')), { once: true });
    });
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('data-kakao-map-sdk', 'true');
  script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false${
    libraries.length ? `&libraries=${libraries.join(',')}` : ''
  }`;
  script.async = true;

  const scriptPromise = new Promise<void>((resolve, reject) => {
    script.onload = () => {
      window.kakao.maps.load(() => resolve());
    };
    script.onerror = () => reject(new Error('카카오맵 SDK 로드 실패'));
  });

  document.head.appendChild(script);

  await scriptPromise;
};

const KakaoMapViewer: React.FC<KakaoMapViewerProps> = ({
  data,
  height = '320px',
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const overlaysRef = useRef<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const hasRenderableData = useMemo(() => {
    if (!data) return false;
    return Boolean(
      (Array.isArray(data.polygon) && data.polygon.length > 0) ||
        (Array.isArray(data.rectangle) && data.rectangle.length > 0) ||
        (Array.isArray(data.circle) && data.circle.length > 0)
    );
  }, [data]);

  useEffect(() => {
    let isMounted = true;

    const initializeMap = async () => {
      if (!data || !containerRef.current) {
        setIsLoading(false);
        return;
      }

      if (!hasRenderableData) {
        setIsLoading(false);
        return;
      }

      try {
        await loadKakaoMapsSdk(['services']);

        if (!isMounted || !containerRef.current) {
          return;
        }

        const kakao = window.kakao;
        const container = containerRef.current;
        const options = {
          center: new kakao.maps.LatLng(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng),
          level: DEFAULT_LEVEL
        };

        const map = new kakao.maps.Map(container, options);

        const bounds = new kakao.maps.LatLngBounds();
        let boundsExtended = false;

        const applyPoint = (point: KakaoPoint): any => {
          const latLng = new kakao.maps.LatLng(point.y, point.x);
          bounds.extend(latLng);
          boundsExtended = true;
          return latLng;
        };

        overlaysRef.current.forEach(overlay => overlay.setMap(null));
        overlaysRef.current = [];

        const polygonColorPalette = ['#39f', '#ff5a5f', '#2ecc71', '#f39c12', '#9b59b6'];

        data.polygon?.forEach((polygon, index) => {
          if (!Array.isArray(polygon.points) || polygon.points.length < 3) {
            return;
          }

          const path = polygon.points.map(applyPoint);

          const options = polygon.options ?? {};
          const colorSet = polygonColorPalette[index % polygonColorPalette.length];

          const polygonOverlay = new kakao.maps.Polygon({
            map,
            path,
            strokeWeight: options.strokeWeight ?? 3,
            strokeColor: options.strokeColor ?? colorSet,
            strokeOpacity: options.strokeOpacity ?? 1,
            strokeStyle: options.strokeStyle ?? 'solid',
            fillColor: options.fillColor ?? colorSet,
            fillOpacity: options.fillOpacity ?? 0.35
          });

          overlaysRef.current.push(polygonOverlay);
        });

        data.rectangle?.forEach((rectangle, index) => {
          if (!rectangle.sPoint || !rectangle.ePoint) {
            return;
          }

          const colorSet = polygonColorPalette[(index + 2) % polygonColorPalette.length];
          const options = rectangle.options ?? {};

          const boundsRect = new kakao.maps.LatLngBounds(
            applyPoint(rectangle.sPoint),
            applyPoint(rectangle.ePoint)
          );

          const rectangleOverlay = new kakao.maps.Rectangle({
            map,
            bounds: boundsRect,
            strokeWeight: options.strokeWeight ?? 3,
            strokeColor: options.strokeColor ?? colorSet,
            strokeOpacity: options.strokeOpacity ?? 1,
            strokeStyle: options.strokeStyle ?? 'solid',
            fillColor: options.fillColor ?? colorSet,
            fillOpacity: options.fillOpacity ?? 0.35
          });

          overlaysRef.current.push(rectangleOverlay);
        });

        data.circle?.forEach((circle, index) => {
          const centerPoint = circle.center ?? circle.sPoint ?? circle.ePoint;
          if (!centerPoint) {
            return;
          }

          const center = applyPoint(centerPoint);

          let radius = circle.radius ?? null;
          if (radius === null && circle.sPoint && circle.ePoint) {
            const dx = circle.ePoint.x - circle.sPoint.x;
            const dy = circle.ePoint.y - circle.sPoint.y;
            radius = Math.sqrt(dx * dx + dy * dy) * 55000;
          }
          if (radius === null || Number.isNaN(radius)) {
            radius = 50;
          }

          const options = circle.options ?? {};
          const colorSet = polygonColorPalette[(index + 4) % polygonColorPalette.length];

          const circleOverlay = new kakao.maps.Circle({
            map,
            center,
            radius,
            strokeWeight: options.strokeWeight ?? 3,
            strokeColor: options.strokeColor ?? colorSet,
            strokeOpacity: options.strokeOpacity ?? 1,
            strokeStyle: options.strokeStyle ?? 'solid',
            fillColor: options.fillColor ?? colorSet,
            fillOpacity: options.fillOpacity ?? 0.25
          });

          const anchorPoint = circle.ePoint ?? circle.sPoint;
          if (anchorPoint) {
            applyPoint(anchorPoint);
          }

          overlaysRef.current.push(circleOverlay);
        });

        if (boundsExtended) {
          map.setBounds(bounds);
        } else {
          map.setCenter(new kakao.maps.LatLng(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng));
        }

        setIsLoading(false);
      } catch (err) {
        console.error(err);
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : '지도를 불러오는 중 오류가 발생했습니다.');
        setIsLoading(false);
      }
    };

    initializeMap();

    return () => {
      isMounted = false;
      overlaysRef.current.forEach(overlay => {
        if (overlay && typeof overlay.setMap === 'function') {
          overlay.setMap(null);
        }
      });
      overlaysRef.current = [];
    };
  }, [data, hasRenderableData]);

  if (!data) {
    return null;
  }

  if (error) {
    return (
      <div className={`rounded-md bg-red-50 text-red-600 text-sm p-3 ${className}`}>
        {error}
      </div>
    );
  }

  if (!hasRenderableData) {
    return (
      <div className={`rounded-md bg-gray-50 text-gray-500 text-sm p-3 ${className}`}>
        표시할 지도 정보가 없습니다.
      </div>
    );
  }

  return (
    <div className={`relative rounded-md border border-gray-200 overflow-hidden ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-sm text-gray-500">
          지도를 불러오는 중입니다...
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height }} />
    </div>
  );
};

export default KakaoMapViewer;
